import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Solar } from 'lunar-typescript';
import XuanYuanTopBar from './XuanYuanTopBar';

interface WanNianLiAppProps {
  onBack: () => void;
}

interface CalendarCell {
  date: Date;
  key: string;
  year: number;
  month: number;
  day: number;
  isCurrentMonth: boolean;
  isWeekend: boolean;
  displayLunarText: string;
  displayType: 'festival' | 'jieqi' | 'normal';
  dayGanZhi: string;
}

interface AlmanacField {
  label: string;
  value: string;
}

interface TimeLuckRow {
  ganZhi: string;
  luck: string;
  yi: string[];
  ji: string[];
}

const WEEK_LABELS = ['日', '一', '二', '三', '四', '五', '六'];
const PAD_TITLE_FONT = "font-['STKaiti','Kaiti_SC','KaiTi','serif']";

const pad2 = (n: number): string => String(n).padStart(2, '0');

const toDateOnly = (value: Date): Date => (
  new Date(value.getFullYear(), value.getMonth(), value.getDate())
);

const toDateKey = (date: Date): string => (
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
);

const formatMonthLabel = (date: Date): string => `${date.getFullYear()}年${date.getMonth() + 1}月`;

const formatDateText = (date: Date): string => `${pad2(date.getMonth() + 1)}月${pad2(date.getDate())}日`;

const truncate = (text: string, max = 4): string => {
  const value = String(text || '').trim();
  if (!value) return '--';
  return value.length > max ? `${value.slice(0, max)}…` : value;
};

const formatXiongSha = (items: string[]): string => {
  if (!items.length) return '--';
  if (items.length <= 6) return items.join(' ');
  const mid = Math.ceil(items.length / 2);
  return `${items.slice(0, mid).join(' ')}\n${items.slice(mid).join(' ')}`;
};

const createCell = (date: Date, currentMonth: number): CalendarCell => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const solar = Solar.fromYmd(year, month, day);
  const lunar = solar.getLunar();
  const lunarFestivals = [...lunar.getFestivals(), ...lunar.getOtherFestivals()].filter(Boolean);
  const jieQi = String(lunar.getJieQi() || '').trim();
  const lunarDayText = lunar.getDayInChinese();

  let displayType: CalendarCell['displayType'] = 'normal';
  let displayLunarText = lunarDayText;

  if (lunarFestivals.length > 0) {
    displayType = 'festival';
    displayLunarText = lunarFestivals[0];
  } else if (jieQi) {
    displayType = 'jieqi';
    displayLunarText = jieQi;
  }

  return {
    date,
    key: toDateKey(date),
    year,
    month,
    day,
    isCurrentMonth: date.getMonth() === currentMonth,
    isWeekend: date.getDay() === 0 || date.getDay() === 6,
    displayLunarText,
    displayType,
    dayGanZhi: lunar.getDayInGanZhiExact(),
  };
};

const buildMonthCells = (monthAnchor: Date): CalendarCell[] => {
  const firstDay = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1);
  const daysInMonth = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 0).getDate();
  const weekOffset = firstDay.getDay();
  const totalCells = Math.ceil((weekOffset + daysInMonth) / 7) * 7;
  const start = new Date(firstDay.getFullYear(), firstDay.getMonth(), 1 - weekOffset);

  return Array.from({ length: totalCells }, (_, idx) => {
    const target = new Date(start.getFullYear(), start.getMonth(), start.getDate() + idx);
    return createCell(target, monthAnchor.getMonth());
  });
};

const monthShift = (date: Date, delta: number): Date => new Date(date.getFullYear(), date.getMonth() + delta, 1);

const TableCell: React.FC<AlmanacField & {
  noBorderRight?: boolean;
  noBorderBottom?: boolean;
  valueClassName?: string;
}> = ({
  label,
  value,
  noBorderRight,
  noBorderBottom,
  valueClassName,
}) => (
  <div className={`p-2 text-center ${noBorderRight ? '' : 'border-r border-[#e2e2e2]'} ${noBorderBottom ? '' : 'border-b border-[#e2e2e2]'}`}>
    <div className="text-[12px] leading-5 text-[#b44f54]">{label}</div>
    <div className={`mt-0.5 text-[12px] leading-5 text-[#2f2f2f] break-words ${valueClassName || ''}`}>{value || '--'}</div>
  </div>
);

const WanNianLiApp: React.FC<WanNianLiAppProps> = ({ onBack }) => {
  const now = useMemo(() => new Date(), []);
  const today = useMemo(() => toDateOnly(now), [now]);
  const [viewMonth, setViewMonth] = useState<Date>(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState<Date>(today);
  const [showHuangLi, setShowHuangLi] = useState(false);
  const [activeTimeIndex, setActiveTimeIndex] = useState<number | null>(null);

  const cells = useMemo(() => buildMonthCells(viewMonth), [viewMonth]);
  const todayKey = toDateKey(today);
  const selectedKey = toDateKey(selectedDate);

  useEffect(() => {
    setActiveTimeIndex(null);
  }, [selectedKey]);

  const selectedHour = selectedKey === todayKey ? new Date().getHours() : 12;
  const selectedSolar = useMemo(
    () => Solar.fromYmdHms(selectedDate.getFullYear(), selectedDate.getMonth() + 1, selectedDate.getDate(), selectedHour, 0, 0),
    [selectedDate, selectedHour],
  );
  const selectedLunar = useMemo(() => selectedSolar.getLunar(), [selectedSolar]);

  const summaryYi = useMemo(() => selectedLunar.getDayYi(1).slice(0, 5), [selectedLunar]);
  const summaryJi = useMemo(() => selectedLunar.getDayJi(1).slice(0, 5), [selectedLunar]);

  const detailYi = useMemo(() => selectedLunar.getDayYi(1).slice(0, 12), [selectedLunar]);
  const detailJi = useMemo(() => selectedLunar.getDayJi(1).slice(0, 12), [selectedLunar]);
  const jiShen = useMemo(() => selectedLunar.getDayJiShen().slice(0, 12), [selectedLunar]);
  const xiongSha = useMemo(() => selectedLunar.getDayXiongSha().slice(0, 12), [selectedLunar]);

  const dayNineStar = useMemo(() => selectedLunar.getDayNineStar(), [selectedLunar]);

  const tableRows3: AlmanacField[][] = useMemo(() => [
    [
      { label: '值神', value: `${selectedLunar.getDayTianShen()}(${selectedLunar.getDayTianShenType()}日)` },
      { label: '五行', value: selectedLunar.getDayNaYin() },
      { label: '冲煞', value: `${selectedLunar.getDayChongDesc()} 煞${selectedLunar.getDaySha()}` },
    ],
    [
      { label: '喜神', value: selectedLunar.getDayPositionXiDesc() },
      { label: '福神', value: selectedLunar.getDayPositionFuDesc() },
      { label: '财神', value: selectedLunar.getDayPositionCaiDesc() },
    ],
    [
      { label: '阳贵神', value: selectedLunar.getDayPositionYangGuiDesc() },
      { label: '胎神', value: selectedLunar.getDayPositionTai() },
      { label: '阴贵神', value: selectedLunar.getDayPositionYinGuiDesc() },
    ],
    [
      { label: '二十八星宿', value: `${selectedLunar.getGong()}方${selectedLunar.getXiu()}日${selectedLunar.getAnimal()}-${selectedLunar.getXiuLuck()}` },
      { label: '建除十二神', value: `${selectedLunar.getZhiXing()}日` },
      { label: '命禄', value: selectedLunar.getDayLu() },
    ],
    [
      { label: '月相', value: selectedLunar.getYueXiang() },
      { label: '物候', value: selectedLunar.getWuHou() },
      { label: '六耀', value: selectedLunar.getLiuYao() },
    ],
  ], [selectedLunar]);

  const doubleRows: [AlmanacField, AlmanacField][] = useMemo(() => [
    [
      { label: '吉神宜趋', value: jiShen.length ? jiShen.join(' ') : '--' },
      { label: '凶神宜忌', value: formatXiongSha(xiongSha) },
    ],
    [
      { label: '彭祖百忌', value: `${selectedLunar.getPengZuGan()} ${selectedLunar.getPengZuZhi()}` },
      { label: '九宫飞星', value: `${dayNineStar.getNumber()}${dayNineStar.getColor()} ${dayNineStar.getNameInBeiDou()}(${dayNineStar.getWuXing()}) ${dayNineStar.getLuckInXuanKong()}` },
    ],
  ], [dayNineStar, jiShen, selectedLunar, xiongSha]);

  const timeLuckRows: TimeLuckRow[] = useMemo(() => (
    selectedLunar
      .getTimes()
      .slice(0, 12)
      .map((item) => ({
        ganZhi: item.getGanZhi(),
        luck: item.getTianShenLuck(),
        yi: item.getYi(),
        ji: item.getJi(),
      }))
  ), [selectedLunar]);

  const hasTimeYiJi = useMemo(
    () => timeLuckRows.some((row) => row.yi.length > 0 || row.ji.length > 0),
    [timeLuckRows],
  );

  const activeTime = activeTimeIndex !== null ? timeLuckRows[activeTimeIndex] || null : null;

  const huangLiPanel = (
    <div className="bg-white">
      <div className="px-2 py-2.5 text-center">
        <div className={`text-[32px] font-semibold leading-none text-[#c73f46] ${PAD_TITLE_FONT}`}>
          {selectedLunar.getMonthInChinese()}月{selectedLunar.getDayInChinese()}
        </div>
        <div className="mt-1.5 text-[13px] font-semibold text-[#5a5a5a]">
          {selectedLunar.getYearInGanZhiExact()}年 {selectedLunar.getMonthInGanZhiExact()}月 {selectedLunar.getDayInGanZhiExact()}日 星期{selectedSolar.getWeekInChinese()}
        </div>
      </div>

      <div className="px-2 pb-1">
        <div className="mb-1.5 flex items-start text-[13px] leading-6 text-[#444]">
          <span className="mr-2 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#2ac6b8] text-[11px] font-bold text-white">宜</span>
          <span>{detailYi.length ? detailYi.join(' ') : '无'}</span>
        </div>
        <div className="mb-1.5 flex items-start text-[13px] leading-6 text-[#444]">
          <span className="mr-2 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#d40202] text-[11px] font-bold text-white">忌</span>
          <span>{detailJi.length ? detailJi.join(' ') : '无'}</span>
        </div>
      </div>

      <div className="mx-1 mb-1 border border-[#e2e2e2]">
        {tableRows3.map((row, rowIdx) => (
          <div key={`row3-${rowIdx}`} className="grid grid-cols-3">
            {row.map((item, colIdx) => (
              <TableCell
                key={`${item.label}-${colIdx}`}
                label={item.label}
                value={item.value}
                noBorderRight={colIdx === row.length - 1}
                noBorderBottom={rowIdx === tableRows3.length - 1}
                valueClassName={item.label === '胎神' ? 'whitespace-nowrap text-[11px]' : ''}
              />
            ))}
          </div>
        ))}
      </div>

      <div className="mx-1 mb-1 border border-[#e2e2e2]">
        {doubleRows.map((pair, idx) => (
          <div key={`pair-${idx}`} className="grid grid-cols-2">
            <TableCell
              label={pair[0].label}
              value={pair[0].value}
              noBorderRight={false}
              noBorderBottom={idx === doubleRows.length - 1}
            />
            <TableCell
              label={pair[1].label}
              value={pair[1].value}
              noBorderRight
              noBorderBottom={idx === doubleRows.length - 1}
              valueClassName={pair[1].label === '凶神宜忌' ? 'whitespace-pre-line' : ''}
            />
          </div>
        ))}
      </div>

      <div className="mx-1 mb-1 border border-[#e2e2e2] bg-white">
        <div className="border-b border-[#e2e2e2] py-1.5 text-center text-[12px] text-[#b44f54]">时辰吉凶</div>
        <div className="grid grid-cols-12">
          {timeLuckRows.map((item, idx) => (
            <button
              key={`time-box-${idx}`}
              type="button"
              onClick={() => setActiveTimeIndex(idx)}
              className={`px-0.5 py-1 text-center ${idx !== timeLuckRows.length - 1 ? 'border-r border-[#e2e2e2]' : ''} ${activeTimeIndex === idx ? 'bg-rose-50' : 'bg-white'}`}
            >
              <div
                className={`mx-auto text-[10px] leading-[1.08] [writing-mode:vertical-rl] [text-orientation:upright] ${
                  item.luck.includes('吉') ? 'text-[#c24649]' : 'text-[#8d8d8d]'
                }`}
              >
                {item.ganZhi}{item.luck}
              </div>
            </button>
          ))}
        </div>
      </div>

      {hasTimeYiJi && (
        <div className="mx-1 mb-1 border border-[#e2e2e2] bg-white p-2 text-center">
          <p className="text-[12px] text-[#9a9a9a]">点击查看时辰宜忌</p>
          {activeTime && (
            <div className="mt-1 rounded border border-[#f0f0f0] bg-[#fafafa] p-2 text-left">
              <p className="text-[13px] font-semibold text-[#444]">{activeTime.ganZhi}时</p>
              <p className="mt-1 text-[12px] leading-5 text-emerald-700">宜：{activeTime.yi.length ? activeTime.yi.join('、') : '无'}</p>
              <p className="mt-0.5 text-[12px] leading-5 text-rose-700">忌：{activeTime.ji.length ? activeTime.ji.join('、') : '无'}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const handleSelectDate = (cell: CalendarCell) => {
    const next = toDateOnly(cell.date);
    setSelectedDate(next);
    if (!cell.isCurrentMonth) {
      setViewMonth(new Date(cell.year, cell.month - 1, 1));
    }
  };

  const openDetail = () => setShowHuangLi(true);
  const closeDetail = () => setShowHuangLi(false);

  return (
    <div className="h-full overflow-y-auto bg-[#f4f4f4]">
      <XuanYuanTopBar
        title="轩辕国学·万年历"
        onBack={onBack}
        variant="light"
        maxWidthClassName="max-w-[980px]"
      />
      <div className="mx-auto max-w-[980px] pb-6">

        <section className="bg-white">
          <div className="flex items-center justify-between bg-[#eb5154] px-4 py-2 text-white">
            <button
              onClick={() => setViewMonth((prev) => monthShift(prev, -1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full transition hover:bg-white/15"
              aria-label="上一月"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="text-[20px] font-medium">{formatMonthLabel(viewMonth)}</div>
            <button
              onClick={() => setViewMonth((prev) => monthShift(prev, 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full transition hover:bg-white/15"
              aria-label="下一月"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          <div className="grid grid-cols-7 border-b border-[#ececec] bg-[#f7f7f7] px-1.5 py-1">
            {WEEK_LABELS.map((label) => (
              <div key={label} className={`py-0.5 text-center text-[12px] ${label === '日' || label === '六' ? 'text-[#c24c4f]' : 'text-[#4a4a4a]'}`}>
                {label}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 border-l border-t border-[#ececec] bg-white">
            {cells.map((cell) => {
              const isSelected = cell.key === selectedKey;
              const isToday = cell.key === todayKey;
              const lineColor = cell.displayType === 'festival'
                ? 'text-[#c93f42]'
                : cell.displayType === 'jieqi'
                  ? 'text-[#c93f42]'
                  : 'text-[#929292]';

              return (
                <button
                  key={cell.key}
                  onClick={() => handleSelectDate(cell)}
                  className={`h-[58px] border-b border-r border-[#ececec] px-0.5 py-1 text-center transition ${
                    isSelected
                      ? 'bg-[#21bfb8] text-white'
                      : isToday
                        ? 'bg-[#ecf9f8]'
                        : ''
                  } ${cell.isCurrentMonth ? '' : 'opacity-35'}`}
                >
                  <div className={`text-[17px] font-semibold leading-none ${isSelected ? 'text-white' : cell.isWeekend ? 'text-[#c93f42]' : 'text-[#202020]'}`}>
                    {cell.day}
                  </div>
                  <div className={`mt-0.5 text-[10px] leading-4 ${isSelected ? 'text-white/95' : lineColor}`}>
                    {truncate(cell.displayLunarText, 4)}
                  </div>
                  <div className={`text-[10px] leading-4 ${isSelected ? 'text-white/95' : 'text-[#9d9d9d]'}`}>
                    {cell.dayGanZhi}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <section className="mx-3 mt-3 overflow-hidden rounded-2xl border border-[#e5e5e5] bg-white shadow-[0_6px_18px_rgba(0,0,0,0.06)]">
          <div className="flex items-center border-b border-[#efefef] px-3 py-2.5">
            <div className="mr-2.5 text-[32px] leading-none text-[#ed2f2f]">{pad2(selectedDate.getDate())}</div>
            <div className="flex-1">
              <div className={`text-[20px] font-semibold text-[#222] ${PAD_TITLE_FONT}`}>{selectedLunar.getMonthInChinese()}月{selectedLunar.getDayInChinese()}</div>
              <div className="mt-0.5 text-[12px] text-[#4a4a4a]">
                {selectedLunar.getYearInGanZhiExact()}年 {selectedLunar.getMonthInGanZhiExact()}月 {selectedLunar.getDayInGanZhiExact()}日 [{selectedLunar.getYearShengXiaoExact()}] 星期{selectedSolar.getWeekInChinese()}
              </div>
              <div className="text-[11px] text-[#666]">日支：{selectedLunar.getDayZhi()} · 日期：{formatDateText(selectedDate)}</div>
            </div>
          </div>

          <div className="space-y-1.5 px-3 py-2.5">
            <div className="flex items-start text-[13px] leading-6 text-[#3a3a3a]">
              <span className="mr-2 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#2ac6b8] text-[11px] font-bold text-white">宜</span>
              <span>{summaryYi.length ? summaryYi.join(' ') : '无'}</span>
            </div>
            <div className="flex items-start text-[13px] leading-6 text-[#3a3a3a]">
              <span className="mr-2 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#d40202] text-[11px] font-bold text-white">忌</span>
              <span>{summaryJi.length ? summaryJi.join(' ') : '无'}</span>
            </div>
          </div>

          <button
            onClick={openDetail}
            className="w-full bg-[#d09261] py-2 text-[15px] font-medium tracking-wide text-white transition hover:bg-[#c78554]"
          >
            点击查看今天的黄历
          </button>
        </section>
      </div>

      {showHuangLi && (
        <div className="fixed inset-0 z-[120] bg-black/45">
          <div className="h-full w-full overflow-y-auto bg-[#f4f4f4]">
            <header className="sticky top-0 z-20 flex items-center justify-between border-b border-[#e8e8e8] bg-white px-4 py-3">
              <button
                onClick={closeDetail}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-600 transition hover:bg-slate-100"
                aria-label="关闭"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h2 className={`text-[18px] font-semibold text-[#222] ${PAD_TITLE_FONT}`}>今日黄历</h2>
              <button
                onClick={closeDetail}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100"
                aria-label="关闭"
              >
                <X className="h-5 w-5" />
              </button>
            </header>

            <div className="mx-auto max-w-[820px] px-1 pb-4 pt-1">
              {huangLiPanel}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WanNianLiApp;
